import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DocumentsManager } from '../components/admin/DocumentsManager';
import { VenuesTitleEditor } from '../components/admin/VenuesTitleEditor';
import { TimeRangeInput, VenueSelect } from '../components/admin/GameFormFields';
import { PlayerPhotoButton, uploadPlayerPhoto } from '../components/PlayerPhoto';
import { X, Calendar, Trophy, Users, Youtube, FileText, Upload, UserPlus, Edit2, Trash2, Plus, Save, XCircle, Settings, BarChart3, Megaphone, Image, Pin, Camera, ArrowRightLeft, BookOpen, MapPin, Info, Star, ExternalLink, AlertCircle, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { API, BACKEND_URL, getLogoUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const hasScore = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(parseInt(v));

const AdminPage = () => {
  const { isAdmin, token } = useAuth();
  const [activeTab, setActiveTab] = useState('teams');
  const [adminSeason, setAdminSeason] = useState(1);
  const seasonRef = useRef(1);
  seasonRef.current = adminSeason;
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [editingByeWeek, setEditingByeWeek] = useState(null);
  const [editingByeValue, setEditingByeValue] = useState('');
  const [newPlayer, setNewPlayer] = useState(null);
  const [newGame, setNewGame] = useState(null);
  const [newTeam, setNewTeam] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {type: 'team'|'player'|'game', id: string, name: string}
  const [resetConfirm, setResetConfirm] = useState(false);
  const [seasonSettings, setSeasonSettings] = useState({
    teams_count: '8',
    players_count: '80+',
    games_count: '32',
    champion: '1'
  });
  const [season2Settings, setSeason2Settings] = useState({
    teams_count: '0', players_count: '0', games_count: '0', champion: '-'
  });
  const [editingSeason2Settings, setEditingSeason2Settings] = useState(null);
  const [editingSeasonSettings, setEditingSeasonSettings] = useState(null);
  const [editingStats, setEditingStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [galleryAlbums, setGalleryAlbums] = useState([]);
  const [newAlbum, setNewAlbum] = useState(null);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [addingPhotoTo, setAddingPhotoTo] = useState(null);
  const [photoInput, setPhotoInput] = useState({ url: '', caption: '', mode: 'url' });
  const [videos, setVideos] = useState([]);
  const [newVideo, setNewVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [transferringPlayer, setTransferringPlayer] = useState(null);
  const [transferTargetTeam, setTransferTargetTeam] = useState('');
  const [aboutSections, setAboutSections] = useState({});
  const [editingAbout, setEditingAbout] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [venuesList, setVenuesList] = useState([]);
  const [editingVenue, setEditingVenue] = useState(null);
  const [newVenue, setNewVenue] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [newSponsor, setNewSponsor] = useState(null);
  const [scheduleNotices, setScheduleNotices] = useState([]);
  const [editingNotice, setEditingNotice] = useState(null);
  const [newNotice, setNewNotice] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncPolling, setSyncPolling] = useState(false);
  const [gameStatsModal, setGameStatsModal] = useState(null);
  const [gameStatsData, setGameStatsData] = useState({});
  const sponsorFileRef = useRef(null);
  const newSponsorFileRef = useRef(null);
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);
  const photoFileRef = useRef(null);
  const attachmentFileRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = () => {
    const s = adminSeason;
    const forSeason = (setter) => (res) => { if (seasonRef.current === s) setter(res.data); };
    axios.get(`${API}/teams?season=${s}`).then(forSeason(setTeams)).catch(console.error);
    axios.get(`${API}/games?season=${s}`).then(forSeason(setGames)).catch(console.error);
    axios.get(`${API}/settings/season`).then(res => setSeasonSettings(res.data)).catch(console.error);
    axios.get(`${API}/settings/season2`).then(res => setSeason2Settings(res.data)).catch(console.error);
    axios.get(`${API}/announcements`).then(res => setAnnouncements(res.data)).catch(console.error);
    axios.get(`${API}/gallery?season=${s}`).then(forSeason(setGalleryAlbums)).catch(console.error);
    axios.get(`${API}/videos?season=${s}`).then(forSeason(setVideos)).catch(console.error);
    axios.get(`${API}/about-content`).then(res => setAboutSections(res.data.sections || {})).catch(console.error);
    axios.get(`${API}/faq`).then(res => setFaqItems(res.data.items || [])).catch(console.error);
    axios.get(`${API}/venues`).then(res => setVenuesList(res.data)).catch(console.error);
    axios.get(`${API}/sponsors`).then(res => setSponsors(res.data)).catch(console.error);
    axios.get(`${API}/schedule-notices?season=${s}`).then(res => { if (seasonRef.current === s) setScheduleNotices(res.data.notices || []); }).catch(console.error);
  };

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin, adminSeason]);

  if (!isAdmin) return <Navigate to="/login" />;

  // Logo Upload
  const handleLogoUpload = async (teamId, file) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/teams/${teamId}/logo`, {
          team_id: teamId,
          logo_base64: reader.result
        }, { headers });
        loadData();
        setUploadingLogo(null);
      } catch (err) { console.error(err); }
    };
    reader.readAsDataURL(file);
  };

  // Team Management
  const saveTeam = async () => {
    if (!editingTeam) return;
    try {
      await axios.put(`${API}/teams/${editingTeam.id}`, {
        name: editingTeam.name,
        coach: editingTeam.coach
      }, { headers });
      setEditingTeam(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const createTeam = async () => {
    if (!newTeam || !newTeam.name || !newTeam.short_name || !newTeam.color) return;
    try {
      await axios.post(`${API}/teams`, {...newTeam, season: adminSeason}, { headers });
      setNewTeam(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  // Confirm Delete Handler
  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      if (deleteConfirm.type === 'team') {
        await axios.delete(`${API}/teams/${deleteConfirm.id}`, { headers });
        loadData();
      } else if (deleteConfirm.type === 'player' && selectedTeam) {
        await axios.delete(`${API}/teams/${selectedTeam.id}/players/${deleteConfirm.id}`, { headers });
        loadData();
        const updated = await axios.get(`${API}/teams/${selectedTeam.id}`);
        setSelectedTeam(updated.data);
      } else if (deleteConfirm.type === 'game') {
        await axios.delete(`${API}/games/${deleteConfirm.id}`, { headers });
        loadData();
      } else if (deleteConfirm.type === 'venue') {
        await axios.delete(`${API}/venues/${deleteConfirm.id}`, { headers });
        loadData();
      } else if (deleteConfirm.type === 'sponsor') {
        await axios.delete(`${API}/sponsors/${deleteConfirm.id}`, { headers });
        loadData();
      }
    } catch (err) { 
      console.error(err); 
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
    setDeleteConfirm(null);
  };

  // Player Management
  const addPlayer = async () => {
    if (!newPlayer || !selectedTeam) return;
    try {
      const { photoFile, ...playerData } = newPlayer;
      const res = await axios.post(`${API}/teams/${selectedTeam.id}/players`, playerData, { headers });
      const created = res.data.players[res.data.players.length - 1];
      if (photoFile && created) {
        try { await uploadPlayerPhoto(created.id, photoFile, headers); } catch (e) { toast.error('Η παίκτρια αποθηκεύτηκε αλλά η φωτογραφία απέτυχε'); }
      }
      setNewPlayer(null);
      loadData();
      // Refresh selected team
      const updated = await axios.get(`${API}/teams/${selectedTeam.id}`);
      setSelectedTeam(updated.data);
    } catch (err) {
      console.error(err);
      toast.error(language === 'gr' ? 'Η αποθήκευση απέτυχε — συμπλήρωσε όνομα και νούμερο' : 'Save failed — name and number are required');
    }
  };

  // Update existing player
  const savePlayer = async () => {
    if (!editingPlayer || !selectedTeam) return;
    try {
      await axios.put(`${API}/teams/${selectedTeam.id}/players/${editingPlayer.id}`, {
        name: editingPlayer.name,
        number: editingPlayer.number,
        position: editingPlayer.position,
        height: editingPlayer.height,
        age: editingPlayer.age
      }, { headers });
      setEditingPlayer(null);
      loadData();
      // Refresh selected team
      const updated = await axios.get(`${API}/teams/${selectedTeam.id}`);
      setSelectedTeam(updated.data);
    } catch (err) {
      console.error(err);
      toast.error(language === 'gr' ? 'Η αποθήκευση απέτυχε' : 'Save failed');
    }
  };

  // Update player stats
  const savePlayerStats = async () => {
    if (!editingStats || !selectedTeam) return;
    try {
      await axios.put(`${API}/teams/${selectedTeam.id}/players/${editingStats.id}/stats`, editingStats.stats, { headers });
      setEditingStats(null);
      loadData();
      const updated = await axios.get(`${API}/teams/${selectedTeam.id}`);
      setSelectedTeam(updated.data);
    } catch (err) { console.error(err); }
  };

  // Game Management
  const saveGame = async () => {
    if (!editingGame) return;
    try {
      // Handle empty video_url - send empty string to clear it
      const videoUrl = editingGame.video_url && editingGame.video_url.trim() !== '' 
        ? editingGame.video_url.trim() 
        : '';
      
      const updatePayload = {
        date: editingGame.date,
        time: editingGame.time,
        venue: editingGame.venue,
        week: parseInt(editingGame.week),
        home_score: hasScore(editingGame.home_score) ? parseInt(editingGame.home_score) : null,
        away_score: hasScore(editingGame.away_score) ? parseInt(editingGame.away_score) : null,
        status: hasScore(editingGame.home_score) && hasScore(editingGame.away_score) ? 'completed' : 'scheduled',
        video_url: videoUrl || null,
        phase: editingGame.phase || '',
        description: editingGame.description || '',
        bye_team: editingGame.bye_team || '',
        forfeit_team: editingGame.forfeit_team || '',
      };
      
      // Include team IDs if they've been changed
      if (editingGame.home_team_id) {
        updatePayload.home_team_id = editingGame.home_team_id;
      }
      if (editingGame.away_team_id) {
        updatePayload.away_team_id = editingGame.away_team_id;
      }
      
      await axios.put(`${API}/games/${editingGame.id}`, updatePayload, { headers });
      setEditingGame(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const createGame = async () => {
    if (!newGame) return;
    try {
      await axios.post(`${API}/games`, {...newGame, season: adminSeason}, { headers });
      setNewGame(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  // Reset all team stats
  const handleResetStats = async () => {
    try {
      await axios.post(`${API}/teams/reset-stats?season=${adminSeason}`, {}, { headers });
      setResetConfirm(false);
      loadData();
    } catch (err) { console.error(err); }
  };

  // Save Season Settings
  const saveSeasonSettings = async () => {
    if (!editingSeasonSettings) return;
    try {
      await axios.put(`${API}/settings/season`, editingSeasonSettings, { headers });
      setEditingSeasonSettings(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const saveSeason2Settings = async () => {
    if (!editingSeason2Settings) return;
    try {
      await axios.put(`${API}/settings/season2`, editingSeason2Settings, { headers });
      setEditingSeason2Settings(null);
      loadData();
    } catch (err) { console.error(err); }
  };


  // Player Transfer
  const handleTransfer = async () => {
    if (!transferringPlayer || !transferTargetTeam) return;
    try {
      await axios.post(`${API}/players/transfer`, {
        player_id: transferringPlayer.id,
        from_team_id: selectedTeam.id,
        to_team_id: transferTargetTeam
      }, { headers });
      setTransferringPlayer(null);
      setTransferTargetTeam('');
      loadData();
      setSelectedTeam(null);
    } catch (err) { console.error(err); alert(err.response?.data?.detail || 'Transfer failed'); }
  };

  // About Content Save
  const saveAboutContent = async () => {
    if (!editingAbout) return;
    try {
      await axios.put(`${API}/about-content`, { sections: editingAbout }, { headers });
      setAboutSections(editingAbout);
      setEditingAbout(null);
    } catch (err) { console.error(err); }
  };

  const aboutFields = [
    { key: 'mission1', label: language === 'gr' ? 'Αποστολή - Παράγραφος 1' : 'Mission - Paragraph 1' },
    { key: 'mission2', label: language === 'gr' ? 'Αποστολή - Παράγραφος 2' : 'Mission - Paragraph 2' },
    { key: 'mission3', label: language === 'gr' ? 'Αποστολή - Παράγραφος 3' : 'Mission - Paragraph 3' },
    { key: 'value1_title', label: language === 'gr' ? 'Αξία 1 - Τίτλος' : 'Value 1 - Title', small: true },
    { key: 'value1_desc', label: language === 'gr' ? 'Αξία 1 - Περιγραφή' : 'Value 1 - Description' },
    { key: 'value2_title', label: language === 'gr' ? 'Αξία 2 - Τίτλος' : 'Value 2 - Title', small: true },
    { key: 'value2_desc', label: language === 'gr' ? 'Αξία 2 - Περιγραφή' : 'Value 2 - Description' },
    { key: 'value3_title', label: language === 'gr' ? 'Αξία 3 - Τίτλος' : 'Value 3 - Title', small: true },
    { key: 'value3_desc', label: language === 'gr' ? 'Αξία 3 - Περιγραφή' : 'Value 3 - Description' },
    { key: 'structure_intro', label: language === 'gr' ? 'Δομή - Εισαγωγή' : 'Structure - Intro' },
    { key: 'phase1_title', label: language === 'gr' ? 'Φάση 1 - Τίτλος' : 'Phase 1 - Title', small: true },
    { key: 'phase1_text', label: language === 'gr' ? 'Φάση 1 - Κείμενο' : 'Phase 1 - Text' },
    { key: 'phase2_title', label: language === 'gr' ? 'Φάση 2 - Τίτλος' : 'Phase 2 - Title', small: true },
    { key: 'phase2_text', label: language === 'gr' ? 'Φάση 2 - Κείμενο' : 'Phase 2 - Text' },
    { key: 'phase3_title', label: language === 'gr' ? 'Φάση 3 - Τίτλος' : 'Phase 3 - Title', small: true },
    { key: 'phase3_text', label: language === 'gr' ? 'Φάση 3 - Κείμενο' : 'Phase 3 - Text' },
  ];

  // FAQ Save
  const saveFaq = async () => {
    if (!editingFaq) return;
    try {
      await axios.put(`${API}/faq`, { items: editingFaq }, { headers });
      setFaqItems(editingFaq);
      setEditingFaq(null);
    } catch (err) { console.error(err); }
  };

  const addFaqItem = () => {
    setEditingFaq([...(editingFaq || []), { id: '', question: '', answer: '', order: (editingFaq || []).length }]);
  };

  const removeFaqItem = (idx) => {
    setEditingFaq(editingFaq.filter((_, i) => i !== idx));
  };

  const updateFaqItem = (idx, field, value) => {
    const updated = [...editingFaq];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditingFaq(updated);
  };

  // Venue Save
  const saveVenue = async () => {
    if (!editingVenue) return;
    try {
      await axios.put(`${API}/venues/${editingVenue.id}`, {
        name: editingVenue.name || '',
        address: editingVenue.address || '',
        map_embed: editingVenue.map_embed || '',
        description: editingVenue.description || '',
        image_url: editingVenue.image_url || ''
      }, { headers });
      setEditingVenue(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const createVenue = async () => {
    if (!newVenue) return;
    try {
      await axios.post(`${API}/venues`, {
        name: newVenue.name || '',
        address: newVenue.address || '',
        map_embed: newVenue.map_embed || '',
        description: newVenue.description || '',
        image_url: newVenue.image_url || ''
      }, { headers });
      setNewVenue(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteVenue = async (venueId) => {
    try {
      await axios.delete(`${API}/venues/${venueId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Sponsor CRUD

  const handleSponsorLogo = (e, isNew) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (isNew) {
        setNewSponsor(prev => ({...prev, logo_base64: ev.target.result}));
      } else {
        setEditingSponsor(prev => ({...prev, logo_base64: ev.target.result}));
      }
    };
    reader.readAsDataURL(file);
  };

  const createSponsor = async () => {
    if (!newSponsor || !newSponsor.name) return;
    try {
      await axios.post(`${API}/sponsors`, newSponsor, { headers });
      setNewSponsor(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const saveSponsor = async () => {
    if (!editingSponsor) return;
    try {
      const data = {};
      if (editingSponsor.name !== undefined) data.name = editingSponsor.name;
      if (editingSponsor.title !== undefined) data.title = editingSponsor.title;
      if (editingSponsor.url !== undefined) data.url = editingSponsor.url;
      if (editingSponsor.logo_base64 !== undefined) data.logo_base64 = editingSponsor.logo_base64;
      if (editingSponsor.order !== undefined) data.order = editingSponsor.order;
      await axios.put(`${API}/sponsors/${editingSponsor.id}`, data, { headers });
      setEditingSponsor(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteSponsor = async (id) => {
    try {
      await axios.delete(`${API}/sponsors/${id}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const tabs = [
    { id: 'teams', label: t('manageTeams'), icon: Users },
    { id: 'players', label: t('managePlayers'), icon: UserPlus },
    { id: 'games', label: t('manageGames'), icon: Calendar },
    { id: 'schedule_notices', label: language === 'gr' ? 'Ανακοινώσεις Προγράμματος' : 'Schedule Notices', icon: AlertCircle },
    { id: 'announcements', label: t('manageAnnouncements'), icon: Megaphone },
    { id: 'gallery', label: t('manageGallery'), icon: Camera },
    { id: 'about', label: language === 'gr' ? 'Σελίδα Σχετικά' : 'About Page', icon: BookOpen },
    { id: 'faq', label: language === 'gr' ? 'Συχνές Ερωτήσεις' : 'FAQ', icon: Info },
    { id: 'venue', label: language === 'gr' ? 'Γήπεδο' : 'Venue', icon: MapPin },
    { id: 'sponsors', label: language === 'gr' ? 'Χορηγοί' : 'Sponsors', icon: Star },
    { id: 'settings', label: t('seasonSettings'), icon: Settings },
    { id: 'sync', label: language === 'gr' ? 'Sync to Live' : 'Sync to Live', icon: RefreshCw }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="admin-page">
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">{t('confirmDelete')}</h3>
                <p className="text-slate-500 text-sm">{deleteConfirm.name}</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
                data-testid="cancel-delete-btn"
              >
                {t('cancel')}
              </button>
              <button 
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium"
                data-testid="confirm-delete-btn"
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Stats Confirmation Modal */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reset-confirm-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Trophy className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">{t('resetStatsConfirm')}</h3>
                <p className="text-slate-500 text-sm" data-testid="reset-confirm-season">{language === 'gr' ? `Μόνο για τις ομάδες της Σεζόν ${adminSeason} — Νίκες, Ήττες, Πόντοι → 0` : `Season ${adminSeason} teams only — Wins, Losses, Points → 0`}</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setResetConfirm(false)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
                data-testid="cancel-reset-btn"
              >
                {t('cancel')}
              </button>
              <button 
                onClick={handleResetStats}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                data-testid="confirm-reset-btn"
              >
                {t('resetStats')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Edit Modal */}
      {editingStats && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="stats-edit-modal">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center">
                  <BarChart3 className="text-[#F97316]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-800">{language === 'gr' ? 'Στατιστικά Παίκτριας' : 'Player Statistics'}</h3>
                  <p className="text-slate-500">{editingStats.name}</p>
                </div>
              </div>
              <button onClick={() => setEditingStats(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Αγώνες' : 'Games'}</label>
                <input type="number" min="0" value={editingStats.stats.games} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, games: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Πόντοι' : 'Points'}</label>
                <input type="number" min="0" value={editingStats.stats.points} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, points: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Εύστοχες Βολές' : 'Free Throws Made'}</label>
                <input type="number" min="0" value={editingStats.stats.free_throws} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, free_throws: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Εύστοχα 2ποντα' : '2-Pointers Made'}</label>
                <input type="number" min="0" value={editingStats.stats.two_pointers} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, two_pointers: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Εύστοχα 3ποντα' : '3-Pointers Made'}</label>
                <input type="number" min="0" value={editingStats.stats.three_pointers} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, three_pointers: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Αμυντικά Ριμπ.' : 'Def Rebounds'}</label>
                <input type="number" min="0" value={editingStats.stats.def_rebounds} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, def_rebounds: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Επιθετικά Ριμπ.' : 'Off Rebounds'}</label>
                <input type="number" min="0" value={editingStats.stats.off_rebounds} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, off_rebounds: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Σύνολο Ριμπ.' : 'Total Rebounds'}</label>
                <input type="number" min="0" value={editingStats.stats.total_rebounds} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, total_rebounds: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Κλεψίματα' : 'Steals'}</label>
                <input type="number" min="0" value={editingStats.stats.steals} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, steals: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{language === 'gr' ? 'Λάθη' : 'Turnovers'}</label>
                <input type="number" min="0" value={editingStats.stats.turnovers} onChange={e => setEditingStats({...editingStats, stats: {...editingStats.stats, turnovers: parseInt(e.target.value) || 0}})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setEditingStats(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
              >
                {t('cancel')}
              </button>
              <button 
                onClick={savePlayerStats}
                className="px-4 py-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] font-medium flex items-center gap-2"
              >
                <Save size={18} /> {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-8">
        <Settings className="text-[#1E3A5F]" size={32} />
        <h1 className="section-title">{t('adminTitle')}</h1>
      </div>

      {/* Season Selector */}
      <div className="flex items-center gap-3 mb-6" data-testid="admin-season-selector">
        <span className="text-sm font-medium text-slate-500">{language === 'gr' ? 'Σεζόν:' : 'Season:'}</span>
        {[1, 2].map(s => (
          <button
            key={s}
            onClick={() => { setAdminSeason(s); setSelectedTeam(null); }}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${adminSeason === s ? 'bg-[#F97316] text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`admin-season-${s}`}
          >
            {language === 'gr' ? `Σεζόν ${s}` : `Season ${s}`}
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {tabs.map(tab => (
          <button 
            key={tab.id} 
            onClick={() => { setActiveTab(tab.id); setSelectedTeam(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition ${activeTab === tab.id ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`admin-tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
        
        {/* Reset Stats Button */}
        <button 
          onClick={() => setResetConfirm(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition bg-orange-100 text-orange-700 hover:bg-orange-200 ml-auto"
          data-testid="reset-stats-btn"
        >
          <Trophy size={18} />
          {t('resetStats')}
        </button>
      </div>

      {/* TEAMS TAB */}
      {activeTab === 'teams' && (
        <div className="space-y-4" data-testid="admin-teams">
          {/* Add Team Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setNewTeam({ name: '', short_name: '', color: '#1E3A5F', coach: '' })}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              data-testid="add-team-btn"
            >
              <Plus size={18} /> {t('addTeam')}
            </button>
          </div>

          {/* New Team Form */}
          {newTeam && (
            <div className="card p-5 bg-green-50 border-green-200 mb-4" data-testid="new-team-form">
              <h3 className="font-bold mb-4">{t('createTeam')}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('teamName')}</label>
                  <input 
                    type="text" 
                    value={newTeam.name} 
                    onChange={e => setNewTeam({...newTeam, name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                    data-testid="new-team-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('shortName')}</label>
                  <input 
                    type="text" 
                    maxLength={3}
                    value={newTeam.short_name} 
                    onChange={e => setNewTeam({...newTeam, short_name: e.target.value.toUpperCase()})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                    placeholder="ATH"
                    data-testid="new-team-short-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('teamColor')}</label>
                  <div className="flex gap-2">
                    <input 
                      type="color" 
                      value={newTeam.color} 
                      onChange={e => setNewTeam({...newTeam, color: e.target.value})}
                      className="w-12 h-10 border border-slate-300 rounded-lg cursor-pointer"
                      data-testid="new-team-color"
                    />
                    <input 
                      type="text" 
                      value={newTeam.color} 
                      onChange={e => setNewTeam({...newTeam, color: e.target.value})}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('coachName')}</label>
                  <input 
                    type="text" 
                    value={newTeam.coach} 
                    onChange={e => setNewTeam({...newTeam, coach: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                    data-testid="new-team-coach"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={createTeam} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-team-btn">
                  <Save size={16} /> {t('create')}
                </button>
                <button onClick={() => setNewTeam(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-new-team-btn">
                  <XCircle size={16} /> {t('cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Teams List */}
          {teams.map(team => (
            <div key={team.id} className="card p-5">
              {editingTeam?.id === team.id ? (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('teamName')}</label>
                      <input 
                        type="text" 
                        value={editingTeam.name} 
                        onChange={e => setEditingTeam({...editingTeam, name: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                        data-testid="edit-team-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('coachName')}</label>
                      <input 
                        type="text" 
                        value={editingTeam.coach || ''} 
                        onChange={e => setEditingTeam({...editingTeam, coach: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                        data-testid="edit-team-coach"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveTeam} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-team-btn">
                      <Save size={18} /> {t('save')}
                    </button>
                    <button onClick={() => setEditingTeam(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-edit-team-btn">
                      <XCircle size={18} /> {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    {team.has_logo ? (
                      <img src={getLogoUrl(team.id)} alt={team.name} className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full text-white font-bold flex items-center justify-center text-lg" style={{ backgroundColor: team.color }}>
                        {team.short_name}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg text-[#1E3A5F]">{team.name}</h3>
                      <p className="text-sm text-slate-500">{t('coach')}: {team.coach || '-'}</p>
                      <p className="text-xs text-slate-400">{team.wins}W - {team.losses}L • {team.players?.length || 0} {t('players')}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input type="file" accept="image/*" className="hidden" ref={fileInputRef}
                      onChange={(e) => {
                        if (e.target.files[0] && uploadingLogo) handleLogoUpload(uploadingLogo, e.target.files[0]);
                      }}
                    />
                    <button 
                      onClick={() => { setUploadingLogo(team.id); fileInputRef.current?.click(); }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] text-sm"
                      data-testid={`upload-logo-${team.id}`}
                    >
                      <Upload size={16} /> {t('uploadLogo')}
                    </button>
                    <button 
                      onClick={() => setEditingTeam(team)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A87] text-sm"
                      data-testid={`edit-team-${team.id}`}
                    >
                      <Edit2 size={16} /> {t('editTeam')}
                    </button>
                    <button 
                      onClick={() => setDeleteConfirm({ type: 'team', id: team.id, name: team.name })}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PLAYERS TAB */}
      {activeTab === 'players' && (
        <div className="grid md:grid-cols-3 gap-6" data-testid="admin-players">
          {/* Team Selector */}
          <div className="md:col-span-1">
            <h3 className="font-bold text-lg mb-4">{t('selectTeam')}</h3>
            <div className="space-y-2">
              {teams.map(team => (
                <button
                  key={team.id}
                  onClick={() => setSelectedTeam(team)}
                  className={`w-full p-3 rounded-lg text-left flex items-center gap-3 transition ${selectedTeam?.id === team.id ? 'bg-[#1E3A5F] text-white' : 'bg-white border hover:bg-slate-50'}`}
                  data-testid={`select-team-${team.id}`}
                >
                  {team.has_logo ? (
                    <img src={getLogoUrl(team.id)} alt={team.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ backgroundColor: team.color }}>
                      {team.short_name}
                    </div>
                  )}
                  <span className="font-medium">{team.name}</span>
                  <span className="ml-auto text-sm opacity-70">({team.players?.length || 0})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Players List */}
          <div className="md:col-span-2">
            {selectedTeam ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">{selectedTeam.name} - {t('roster')}</h3>
                  <button
                    onClick={() => setNewPlayer({ name: '', number: '', position: 'PG', height: '', age: '' })}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                    data-testid="add-player-btn"
                  >
                    <Plus size={16} /> {t('addPlayer')}
                  </button>
                </div>

                {/* Add Player Form */}
                {newPlayer && (
                  <div className="card p-4 mb-4 bg-green-50 border-green-200" data-testid="new-player-form">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                      <input type="text" placeholder={t('playerName')} value={newPlayer.name} onChange={e => setNewPlayer({...newPlayer, name: e.target.value})} className="px-3 py-2 border rounded-lg col-span-2" data-testid="new-player-name" />
                      <input type="number" min="0" placeholder={t('jerseyNumber')} value={newPlayer.number} onChange={e => setNewPlayer({...newPlayer, number: e.target.value === '' ? '' : parseInt(e.target.value)})} className="px-3 py-2 border rounded-lg" data-testid="new-player-number" />
                      <select value={newPlayer.position} onChange={e => setNewPlayer({...newPlayer, position: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="new-player-position">
                        {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => <option key={pos} value={pos}>{pos}</option>)}
                      </select>
                      <input type="text" placeholder={t('height')} value={newPlayer.height} onChange={e => setNewPlayer({...newPlayer, height: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="new-player-height" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <input type="number" placeholder={t('age')} value={newPlayer.age} onChange={e => setNewPlayer({...newPlayer, age: e.target.value === '' ? '' : parseInt(e.target.value)})} className="px-3 py-2 border rounded-lg" data-testid="new-player-age" />
                      <label className="col-span-2 px-3 py-2 border border-dashed rounded-lg text-sm text-slate-600 cursor-pointer hover:bg-orange-50 truncate" data-testid="new-player-photo">
                        {newPlayer.photoFile ? `Φωτογραφία: ${newPlayer.photoFile.name}` : (language === 'gr' ? '+ Φωτογραφία παίκτριας' : '+ Player photo')}
                        <input type="file" accept="image/*" className="hidden" onChange={e => setNewPlayer({...newPlayer, photoFile: e.target.files[0] || null})} data-testid="new-player-photo-input" />
                      </label>
                      <div className="col-span-2 md:col-span-2 flex gap-2 justify-end">
                        <button onClick={addPlayer} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-player-btn">
                          <Save size={16} /> {t('save')}
                        </button>
                        <button onClick={() => setNewPlayer(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-new-player-btn">
                          <XCircle size={16} /> {t('cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Players List */}
                {selectedTeam.players?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTeam.players.map(player => (
                      <div key={player.id} className="card p-3" data-testid={`player-row-${player.id}`}>
                        {editingPlayer?.id === player.id ? (
                          /* Edit Player Form */
                          <div className="space-y-3" data-testid="edit-player-form">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              <input 
                                type="text" 
                                placeholder={t('playerName')} 
                                value={editingPlayer.name} 
                                onChange={e => setEditingPlayer({...editingPlayer, name: e.target.value})} 
                                className="px-3 py-2 border rounded-lg col-span-2" 
                                data-testid="edit-player-name" 
                              />
                              <input 
                                type="number" 
                                min="0"
                                placeholder={t('jerseyNumber')} 
                                value={editingPlayer.number} 
                                onChange={e => setEditingPlayer({...editingPlayer, number: e.target.value === '' ? '' : parseInt(e.target.value)})} 
                                className="px-3 py-2 border rounded-lg" 
                                data-testid="edit-player-number" 
                              />
                              <select 
                                value={editingPlayer.position} 
                                onChange={e => setEditingPlayer({...editingPlayer, position: e.target.value})} 
                                className="px-3 py-2 border rounded-lg" 
                                data-testid="edit-player-position"
                              >
                                {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => <option key={pos} value={pos}>{pos}</option>)}
                              </select>
                              <input 
                                type="text" 
                                placeholder={t('height')} 
                                value={editingPlayer.height || ''} 
                                onChange={e => setEditingPlayer({...editingPlayer, height: e.target.value})} 
                                className="px-3 py-2 border rounded-lg" 
                                data-testid="edit-player-height" 
                              />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              <input 
                                type="number" 
                                placeholder={t('age')} 
                                value={editingPlayer.age || ''} 
                                onChange={e => setEditingPlayer({...editingPlayer, age: e.target.value === '' ? '' : parseInt(e.target.value)})} 
                                className="px-3 py-2 border rounded-lg" 
                                data-testid="edit-player-age" 
                              />
                              <div className="col-span-2 md:col-span-4 flex gap-2 justify-end">
                                <button onClick={savePlayer} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-player-btn">
                                  <Save size={16} /> {t('save')}
                                </button>
                                <button onClick={() => setEditingPlayer(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-edit-player-btn">
                                  <XCircle size={16} /> {t('cancel')}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Player Display */
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <PlayerPhotoButton player={player} headers={headers} onDone={async () => { const u = await axios.get(`${API}/teams/${selectedTeam.id}`); setSelectedTeam(u.data); }} />
                              <span className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-[#1E3A5F]">#{player.number}</span>
                              <div>
                                <p className="font-medium">{player.name}</p>
                                <p className="text-sm text-slate-500">{[player.position, player.height, player.age ? `${t('age')} ${player.age}` : null].filter(Boolean).join(' • ')}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingStats({ id: player.id, name: player.name, stats: player.stats || { games: 0, points: 0, free_throws: 0, two_pointers: 0, three_pointers: 0, def_rebounds: 0, off_rebounds: 0, total_rebounds: 0, assists: 0, steals: 0, turnovers: 0 } })}
                                className="p-2 text-[#F97316] hover:bg-orange-50 rounded-lg"
                                title="Stats"
                                data-testid={`stats-player-${player.id}`}
                              >
                                <BarChart3 size={18} />
                              </button>
                              <button
                                onClick={() => { setTransferringPlayer(player); setTransferTargetTeam(''); }}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                                title={language === 'gr' ? 'Μεταγραφή' : 'Transfer'}
                                data-testid={`transfer-player-${player.id}`}
                              >
                                <ArrowRightLeft size={18} />
                              </button>
                              <button
                                onClick={() => setEditingPlayer({...player})}
                                className="p-2 text-[#1E3A5F] hover:bg-slate-100 rounded-lg"
                                data-testid={`edit-player-${player.id}`}
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm({ type: 'player', id: player.id, name: player.name })}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                data-testid={`delete-player-${player.id}`}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">{t('noPlayers')}</p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-400">
                <div className="text-center">
                  <Users size={48} className="mx-auto mb-2 opacity-50" />
                  <p>{t('selectTeam')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GAMES TAB */}
      {activeTab === 'games' && (
        <div className="space-y-4" data-testid="admin-games">
          {/* Create Game Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setNewGame({ home_team_id: '', away_team_id: '', date: '', time: '18:00-19:30', venue: '', week: 1, season: adminSeason, bye_team: '' })}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              data-testid="create-game-btn"
            >
              <Plus size={18} /> {t('createGame')}
            </button>
          </div>

          {/* New Game Form */}
          {newGame && (
            <div className="card p-5 bg-green-50 border-green-200 mb-4" data-testid="new-game-form">
              <h3 className="font-bold mb-4">{t('createGame')}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('homeTeam')}</label>
                  <select value={newGame.home_team_id} onChange={e => setNewGame({...newGame, home_team_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-home-team">
                    <option value="">{t('selectTeam')}</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('awayTeam')}</label>
                  <select value={newGame.away_team_id} onChange={e => setNewGame({...newGame, away_team_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-away-team">
                    <option value="">{t('selectTeam')}</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('date')}</label>
                  <input type="date" value={newGame.date} onChange={e => setNewGame({...newGame, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('time')}</label>
                  <TimeRangeInput value={newGame.time} onChange={v => setNewGame({...newGame, time: v})} testId="new-game-time" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('venue')}</label>
                  <VenueSelect value={newGame.venue} onChange={v => setNewGame({...newGame, venue: v})} venues={venuesList} testId="new-game-venue" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Αγωνιστική' : 'Matchday'}</label>
                  <input type="number" min="1" value={newGame.week} onChange={e => setNewGame({...newGame, week: parseInt(e.target.value) || 1})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-week" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Φάση' : 'Phase'}</label>
                  <select value={newGame.phase || ''} onChange={e => setNewGame({...newGame, phase: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-phase">
                    <option value="">{language === 'gr' ? `Κανονική περίοδος (${newGame.week || 1}η Αγωνιστική)` : 'Regular season'}</option>
                    <option value="playoff">Playoff</option>
                    <option value="final4">Final 4</option>
                    <option value="finals">Finals</option>
                    <option value="Ημιτελικοί">Ημιτελικοί</option>
                    <option value="Μικρός Τελικός">Μικρός Τελικός</option>
                    <option value="Μεγάλος Τελικός">Μεγάλος Τελικός</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Ρεπό αγωνιστικής' : 'Bye team'}</label>
                  <select value={newGame.bye_team || ''} onChange={e => setNewGame({...newGame, bye_team: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-game-bye">
                    <option value="">{language === 'gr' ? 'Καμία' : 'None'}</option>
                    {teams.map(team => <option key={team.id} value={team.name}>{team.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Περιγραφή (playoff)' : 'Description (playoff)'}</label>
                  <input type="text" value={newGame.description || ''} onChange={e => setNewGame({...newGame, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder={language === 'gr' ? 'π.χ. Μεγάλος Τελικός' : 'e.g. Grand Final'} data-testid="new-game-description" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={createGame} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-game-btn">
                  <Save size={16} /> {t('create')}
                </button>
                <button onClick={() => setNewGame(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-new-game-btn">
                  <XCircle size={16} /> {t('cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Games List - Grouped by Matchweek */}
          {(() => {
            const grouped = games.reduce((acc, game) => {
              const week = game.week || 0;
              if (!acc[week]) acc[week] = [];
              acc[week].push(game);
              return acc;
            }, {});
            const sortedWeeks = Object.keys(grouped).map(Number).sort((a, b) => a - b);

            return sortedWeeks.map(week => {
              const weekGames = grouped[week].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
              const phase = weekGames[0]?.phase || `${week}${language === 'gr' ? 'η Αγωνιστική' : ' Matchday'}`;
              const byeTeam = weekGames[0]?.bye_team || '';
              const isPlayoff = /playoff|final|semi|τελικ|ημιτελ|κατάταξ/i.test(weekGames[0]?.phase || '');
              const isEditingBye = editingByeWeek === week;

              return (
                <div key={week} className="mb-6" data-testid={`admin-week-${week}`}>
                  {/* Matchweek Header */}
                  <div className={`flex items-center gap-3 mb-3 px-1 ${isPlayoff ? 'text-[#F97316]' : 'text-[#1E3A5F]'}`}>
                    {isPlayoff ? <Trophy size={20} /> : <Calendar size={20} />}
                    <h3 className="font-display font-bold text-lg">{phase}</h3>
                  </div>

                  {/* Bye Team - Editable */}
                  {(byeTeam || !isPlayoff) && (
                    <div className="mb-3 bg-slate-100 border border-slate-200 rounded-xl px-4 py-2.5 flex items-center gap-3" data-testid={`admin-bye-${week}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center">
                        <Users size={16} className="text-slate-600" />
                      </div>
                      {isEditingBye ? (
                        <div className="flex items-center gap-2 flex-1">
                          <select
                            value={editingByeValue}
                            onChange={e => setEditingByeValue(e.target.value)}
                            className="px-3 py-1.5 border rounded-lg text-sm flex-1 max-w-xs"
                            data-testid={`bye-select-${week}`}
                          >
                            <option value="">{language === 'gr' ? 'Καμία' : 'None'}</option>
                            {teams.map(team => <option key={team.id} value={team.name}>{team.name}</option>)}
                          </select>
                          <button
                            onClick={async () => {
                              try {
                                const gameId = weekGames[0]?.id;
                                if (gameId) {
                                  await axios.put(`${API}/games/${gameId}`, { bye_team: editingByeValue, week }, { headers });
                                  loadData();
                                }
                                setEditingByeWeek(null);
                              } catch(e) { console.error(e); }
                            }}
                            className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                            data-testid={`bye-save-${week}`}
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={() => setEditingByeWeek(null)}
                            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm"
                          >
                            <XCircle size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 flex-1">
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                              {language === 'gr' ? 'Ρεπό' : 'Bye'}
                            </span>
                            <p className="text-sm font-bold text-slate-700">{byeTeam || (language === 'gr' ? 'Καμία' : 'None')}</p>
                          </div>
                          <button
                            onClick={() => { setEditingByeWeek(week); setEditingByeValue(byeTeam); }}
                            className="ml-auto px-3 py-1.5 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] text-sm flex items-center gap-1"
                            data-testid={`bye-edit-${week}`}
                          >
                            <Edit2 size={14} /> {language === 'gr' ? 'Αλλαγή' : 'Change'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Games in this week */}
                  <div className="space-y-2">
          {weekGames.map(game => (
            <div key={game.id} className={`card p-4 ${isPlayoff ? 'border-l-4 border-[#F97316]' : ''}`} data-testid={`game-row-${game.id}`}>
              {editingGame?.id === game.id ? (
                <div className="space-y-4">
                  {/* Team Selection Row */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('homeTeam')}</label>
                      <select 
                        value={editingGame.home_team_id} 
                        onChange={e => setEditingGame({...editingGame, home_team_id: e.target.value})} 
                        className="w-full px-3 py-2 border rounded-lg" 
                        data-testid="edit-game-home-team"
                      >
                        {editingGame.home_team_id === 'TBD' && (
                          <option value="TBD">{game.home_team_name || 'TBD'}</option>
                        )}
                        {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('awayTeam')}</label>
                      <select 
                        value={editingGame.away_team_id} 
                        onChange={e => setEditingGame({...editingGame, away_team_id: e.target.value})} 
                        className="w-full px-3 py-2 border rounded-lg" 
                        data-testid="edit-game-away-team"
                      >
                        {editingGame.away_team_id === 'TBD' && (
                          <option value="TBD">{game.away_team_name || 'TBD'}</option>
                        )}
                        {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('date')}</label>
                      <input type="date" value={editingGame.date} onChange={e => setEditingGame({...editingGame, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-date" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('time')}</label>
                      <TimeRangeInput value={editingGame.time} onChange={v => setEditingGame({...editingGame, time: v})} testId="edit-game-time" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('venue')}</label>
                      <VenueSelect value={editingGame.venue} onChange={v => setEditingGame({...editingGame, venue: v})} venues={venuesList} testId="edit-game-venue" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Αγωνιστική' : 'Matchday'}</label>
                      <input type="number" min="1" value={editingGame.week} onChange={e => setEditingGame({...editingGame, week: parseInt(e.target.value) || 1})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-week" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Φάση' : 'Phase'}</label>
                      <input type="text" value={editingGame.phase || ''} onChange={e => setEditingGame({...editingGame, phase: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-phase" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Ρεπό' : 'Bye'}</label>
                      <select value={editingGame.bye_team || ''} onChange={e => setEditingGame({...editingGame, bye_team: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-bye">
                        <option value="">{language === 'gr' ? 'Καμία' : 'None'}</option>
                        {teams.map(team => <option key={team.id} value={team.name}>{team.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{teams.find(t => t.id === editingGame.home_team_id)?.name || game.home_team_name}</span>
                      <input type="number" placeholder="Score" value={editingGame.home_score || ''} onChange={e => setEditingGame({...editingGame, home_score: e.target.value})} className="w-20 px-3 py-2 border rounded-lg text-center" data-testid="edit-game-home-score" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{teams.find(t => t.id === editingGame.away_team_id)?.name || game.away_team_name}</span>
                      <input type="number" placeholder="Score" value={editingGame.away_score || ''} onChange={e => setEditingGame({...editingGame, away_score: e.target.value})} className="w-20 px-3 py-2 border rounded-lg text-center" data-testid="edit-game-away-score" />
                    </div>
                    <div>
                      <input type="url" placeholder={t('videoUrl')} value={editingGame.video_url || ''} onChange={e => setEditingGame({...editingGame, video_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-video" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Αποχώρηση / Μηδενισμός (0 βαθμοί)' : 'Forfeit (0 points)'}</label>
                    <select value={editingGame.forfeit_team || ''} onChange={e => setEditingGame({...editingGame, forfeit_team: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-game-forfeit">
                      <option value="">{language === 'gr' ? 'Καμία αποχώρηση' : 'No forfeit'}</option>
                      <option value={editingGame.home_team_id}>{teams.find(t => t.id === editingGame.home_team_id)?.name || game.home_team_name} ({language === 'gr' ? 'Γηπεδούχος' : 'Home'})</option>
                      <option value={editingGame.away_team_id}>{teams.find(t => t.id === editingGame.away_team_id)?.name || game.away_team_name} ({language === 'gr' ? 'Φιλοξενούμενη' : 'Away'})</option>
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={saveGame} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-game-btn">
                      <Save size={16} /> {t('save')}
                    </button>
                    <button onClick={() => setEditingGame(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-edit-game-btn">
                      <XCircle size={16} /> {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="text-sm text-slate-500 min-w-[130px]">
                    {game.date} &bull; {game.time}
                    {game.description && <span className="ml-2 text-[#F97316] font-medium">{game.description}</span>}
                  </div>
                  <div className="flex items-center gap-3 flex-1 justify-center">
                    <div className="flex items-center gap-2 justify-end flex-1">
                      <span className="font-bold text-right">{game.home_team_name || '-'}</span>
                      {game.home_team_id && game.home_team_id !== 'TBD' && (
                        <img src={getLogoUrl(game.home_team_id)} alt="" className="w-7 h-7 object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                    </div>
                    <span className={`px-3 py-1.5 rounded-lg font-bold text-sm ${game.status === 'completed' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {game.status === 'completed' ? `${game.home_score} - ${game.away_score}` : t('vs')}
                    </span>
                    <div className="flex items-center gap-2 justify-start flex-1">
                      {game.away_team_id && game.away_team_id !== 'TBD' && (
                        <img src={getLogoUrl(game.away_team_id)} alt="" className="w-7 h-7 object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <span className="font-bold text-left">{game.away_team_name || '-'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {game.status === 'completed' && game.home_team_id !== 'TBD' && (
                      <button onClick={async () => {
                        const existing = await axios.get(`${API}/game-stats/${game.id}`).then(r => r.data).catch(() => []);
                        const existingMap = {};
                        existing.forEach(e => { existingMap[e.player_id] = e; });
                        setGameStatsData(existingMap);
                        setGameStatsModal(game);
                      }} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-testid={`game-stats-${game.id}`}>
                        <BarChart3 size={16} /> {language === 'gr' ? 'Stats' : 'Stats'}
                      </button>
                    )}
                    <button onClick={() => setEditingGame({...game, video_url: game.video_url || '', phase: game.phase || '', description: game.description || '', bye_team: game.bye_team || '', forfeit_team: game.forfeit_team || ''})} className="flex items-center gap-2 px-3 py-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] text-sm" data-testid={`edit-game-${game.id}`}>
                      <Edit2 size={16} /> {t('editGame')}
                    </button>
                    <button onClick={() => setDeleteConfirm({ type: 'game', id: game.id, name: `${game.home_team_name} vs ${game.away_team_name}` })} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-game-${game.id}`}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              )}
              {game.video_url && !editingGame && (
                <div className="flex items-center gap-2 text-sm mt-3 pt-3 border-t">
                  <Youtube size={16} className="text-red-600" />
                  <a href={game.video_url} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">{t('watchVideo')}</a>
                </div>
              )}
            </div>
          ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* SCHEDULE NOTICES TAB */}
      {activeTab === 'schedule_notices' && (
        <div className="space-y-4" data-testid="admin-schedule-notices">
          <span className="inline-block px-3 py-1.5 rounded-lg bg-orange-50 text-[#F97316] font-semibold text-sm" data-testid="admin-notices-season-label">{language === 'gr' ? `Ανακοινώσεις Προγράμματος Σεζόν ${adminSeason}` : `Schedule Notices Season ${adminSeason}`}</span>
          <p className="text-slate-500 text-sm mb-4">
            {language === 'gr' 
              ? 'Οι ανακοινώσεις εμφανίζονται στην κορυφή της σελίδας Προγράμματος Αγώνων.'
              : 'Notices appear at the top of the Schedule page.'}
          </p>
          <div className="flex justify-end mb-4">
            <button onClick={() => setNewNotice({ title: '', text: '' })} className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A87]" data-testid="add-notice-btn">
              <Plus size={18} /> {language === 'gr' ? 'Νέα Ανακοίνωση' : 'New Notice'}
            </button>
          </div>

          {newNotice && (
            <div className="card p-5 border-2 border-[#F97316]">
              <div className="space-y-3">
                <input type="text" placeholder={language === 'gr' ? 'Τίτλος (προαιρετικό)' : 'Title (optional)'} value={newNotice.title} onChange={e => setNewNotice({...newNotice, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-notice-title" />
                <textarea placeholder={language === 'gr' ? 'Κείμενο ανακοίνωσης...' : 'Notice text...'} value={newNotice.text} onChange={e => setNewNotice({...newNotice, text: e.target.value})} className="w-full px-3 py-2 border rounded-lg h-24" data-testid="new-notice-text" />
                <div className="flex gap-2 justify-end">
                  <button onClick={async () => {
                    if (!newNotice.text.trim()) return;
                    const updated = [...scheduleNotices, { ...newNotice, id: Date.now().toString() }];
                    try {
                      await axios.put(`${API}/schedule-notices?season=${adminSeason}`, { notices: updated }, { headers });
                      setScheduleNotices(updated);
                      setNewNotice(null);
                    } catch(e) { console.error(e); }
                  }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-notice-btn">
                    <Save size={16} /> {t('save')}
                  </button>
                  <button onClick={() => setNewNotice(null)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {scheduleNotices.length === 0 && !newNotice && (
            <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν ανακοινώσεις' : 'No notices'}</p>
          )}

          {scheduleNotices.map((notice, idx) => (
            <div key={notice.id} className="card p-5 bg-amber-50 border border-amber-200" data-testid={`notice-${notice.id}`}>
              {editingNotice?.id === notice.id ? (
                <div className="space-y-3">
                  <input type="text" value={editingNotice.title} onChange={e => setEditingNotice({...editingNotice, title: e.target.value})} placeholder={language === 'gr' ? 'Τίτλος' : 'Title'} className="w-full px-3 py-2 border rounded-lg" />
                  <textarea value={editingNotice.text} onChange={e => setEditingNotice({...editingNotice, text: e.target.value})} className="w-full px-3 py-2 border rounded-lg h-24" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={async () => {
                      const updated = scheduleNotices.map(n => n.id === editingNotice.id ? editingNotice : n);
                      try {
                        await axios.put(`${API}/schedule-notices?season=${adminSeason}`, { notices: updated }, { headers });
                        setScheduleNotices(updated);
                        setEditingNotice(null);
                      } catch(e) { console.error(e); }
                    }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                      <Save size={16} /> {t('save')}
                    </button>
                    <button onClick={() => setEditingNotice(null)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      {notice.title && <h4 className="font-bold text-amber-800 mb-1">{notice.title}</h4>}
                      <p className="text-amber-700 text-sm">{notice.text}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setEditingNotice({...notice})} className="p-2 text-[#F97316] hover:bg-amber-100 rounded-lg" data-testid={`edit-notice-${notice.id}`}>
                      <Edit2 size={16} />
                    </button>
                    <button onClick={async () => {
                      const updated = scheduleNotices.filter(n => n.id !== notice.id);
                      try {
                        await axios.put(`${API}/schedule-notices?season=${adminSeason}`, { notices: updated }, { headers });
                        setScheduleNotices(updated);
                      } catch(e) { console.error(e); }
                    }} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-notice-${notice.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ANNOUNCEMENTS TAB */}
      {activeTab === 'announcements' && (
        <div className="space-y-4" data-testid="admin-announcements">
          <div className="flex justify-end mb-4">
            <button onClick={() => setNewAnnouncement({ title: '', content: '', pinned: false })} className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A87]" data-testid="create-announcement-btn">
              <Plus size={18} /> {t('createAnnouncement')}
            </button>
          </div>

          {newAnnouncement && (
            <div className="card p-6 border-2 border-[#F97316]">
              <h3 className="font-bold mb-4">{t('createAnnouncement')}</h3>
              <input type="text" placeholder={t('announcementTitle')} value={newAnnouncement.title} onChange={e => setNewAnnouncement({...newAnnouncement, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" data-testid="new-ann-title" />
              <textarea placeholder={t('announcementContent')} value={newAnnouncement.content} onChange={e => setNewAnnouncement({...newAnnouncement, content: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-32" data-testid="new-ann-content" />
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input type="checkbox" checked={newAnnouncement.pinned} onChange={e => setNewAnnouncement({...newAnnouncement, pinned: e.target.checked})} />
                <Pin size={16} /> {t('pinned')}
              </label>
              <div className="flex gap-2">
                <button onClick={async () => { try { await axios.post(`${API}/announcements`, newAnnouncement, { headers }); setNewAnnouncement(null); loadData(); } catch(e) { console.error(e); }}} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-announcement-btn"><Save size={16} /></button>
                <button onClick={() => setNewAnnouncement(null)} className="px-4 py-2 bg-slate-200 rounded-lg"><XCircle size={16} /></button>
              </div>
            </div>
          )}

          {announcements.map(ann => (
            <div key={ann.id} className="card p-4" data-testid={`admin-ann-${ann.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {ann.pinned && <Pin size={14} className="text-[#F97316]" />}
                    <h3 className="font-bold">{ann.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">{ann.content}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(ann.created_at).toLocaleDateString('el-GR')}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => setEditingAnnouncement({...ann})} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={16} /></button>
                  <button onClick={async () => { await axios.delete(`${API}/announcements/${ann.id}`, { headers }); loadData(); }} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-ann-${ann.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
              
              {/* Attachments section */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-500">{t('attachments')} ({ann.attachments?.length || 0})</span>
                  <label className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs cursor-pointer hover:bg-blue-100">
                    <Upload size={12} /> {t('addAttachment')}
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = async () => {
                        try {
                          await axios.post(`${API}/announcements/${ann.id}/attachments`, {
                            filename: file.name,
                            file_base64: reader.result,
                            content_type: file.type
                          }, { headers });
                          loadData();
                        } catch(err) { console.error(err); }
                      };
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }} data-testid={`add-attachment-${ann.id}`} />
                  </label>
                </div>
                {ann.attachments?.length > 0 && (
                  <div className="space-y-1">
                    {ann.attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-2 text-sm py-1 px-2 bg-slate-50 rounded">
                        {att.file_type === 'image' ? <Image size={14} className="text-blue-500" /> : <FileText size={14} className="text-red-500" />}
                        <span className="flex-1 truncate text-xs">{att.filename}</span>
                        <button onClick={async () => { await axios.delete(`${API}/announcements/${ann.id}/attachments/${att.id}`, { headers }); loadData(); }} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {editingAnnouncement && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingAnnouncement(null)}>
              <div className="bg-white rounded-xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold mb-4">{language === 'gr' ? 'Επεξεργασία Ανακοίνωσης' : 'Edit Announcement'}</h3>
                <input type="text" value={editingAnnouncement.title} onChange={e => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" />
                <textarea value={editingAnnouncement.content} onChange={e => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-32" />
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input type="checkbox" checked={editingAnnouncement.pinned} onChange={e => setEditingAnnouncement({...editingAnnouncement, pinned: e.target.checked})} />
                  <Pin size={16} /> {t('pinned')}
                </label>
                <div className="flex gap-2">
                  <button onClick={async () => { await axios.put(`${API}/announcements/${editingAnnouncement.id}`, { title: editingAnnouncement.title, content: editingAnnouncement.content, pinned: editingAnnouncement.pinned }, { headers }); setEditingAnnouncement(null); loadData(); }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg"><Save size={16} /> {t('save')}</button>
                  <button onClick={() => setEditingAnnouncement(null)} className="px-4 py-2 bg-slate-200 rounded-lg">{t('cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {announcements.length === 0 && !newAnnouncement && (
            <p className="text-slate-400 text-center py-8">{t('noAnnouncements')}</p>
          )}
        </div>
      )}

      {/* GALLERY TAB */}
      {activeTab === 'gallery' && (
        <div className="space-y-4" data-testid="admin-gallery">
          <div className="flex items-center justify-between mb-4">
            <span className="px-3 py-1.5 rounded-lg bg-orange-50 text-[#F97316] font-semibold text-sm" data-testid="admin-media-season-label">Media Σεζόν {adminSeason}</span>
            <button onClick={() => setNewAlbum({ title: '', week: 1, description: '', season: adminSeason })} className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A87]" data-testid="create-album-btn">
              <Plus size={18} /> {t('createAlbum')}
            </button>
          </div>

          {newAlbum && (
            <div className="card p-6 border-2 border-[#F97316]">
              <h3 className="font-bold mb-4">{t('createAlbum')}</h3>
              <input type="text" placeholder={t('announcementTitle')} value={newAlbum.title} onChange={e => setNewAlbum({...newAlbum, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" data-testid="new-album-title" />
              <input type="number" placeholder={t('week')} value={newAlbum.week} onChange={e => setNewAlbum({...newAlbum, week: parseInt(e.target.value) || 1})} className="w-full px-3 py-2 border rounded-lg mb-3" data-testid="new-album-week" />
              <textarea placeholder={language === 'gr' ? 'Περιγραφή (προαιρετικό)' : 'Description (optional)'} value={newAlbum.description} onChange={e => setNewAlbum({...newAlbum, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-16" data-testid="new-album-desc" />
              <div className="flex gap-2">
                <button onClick={async () => { try { await axios.post(`${API}/gallery`, {...newAlbum, season: adminSeason}, { headers }); setNewAlbum(null); loadData(); } catch(e) { console.error(e); }}} className="px-4 py-2 bg-green-500 text-white rounded-lg" data-testid="save-album-btn"><Save size={16} /></button>
                <button onClick={() => setNewAlbum(null)} className="px-4 py-2 bg-slate-200 rounded-lg"><XCircle size={16} /></button>
              </div>
            </div>
          )}

          {galleryAlbums.map((album, albumIdx) => (
            <div key={album.id} className="card p-6" data-testid={`admin-album-${album.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button disabled={albumIdx === 0} onClick={async () => { const ids = galleryAlbums.map(a => a.id); [ids[albumIdx], ids[albumIdx-1]] = [ids[albumIdx-1], ids[albumIdx]]; await axios.put(`${API}/gallery/reorder-albums`, ids, { headers }); loadData(); }} className="p-0.5 text-slate-400 hover:text-[#1E3A5F] disabled:opacity-30" data-testid={`album-up-${album.id}`}><ChevronUp size={16} /></button>
                    <button disabled={albumIdx === galleryAlbums.length - 1} onClick={async () => { const ids = galleryAlbums.map(a => a.id); [ids[albumIdx], ids[albumIdx+1]] = [ids[albumIdx+1], ids[albumIdx]]; await axios.put(`${API}/gallery/reorder-albums`, ids, { headers }); loadData(); }} className="p-0.5 text-slate-400 hover:text-[#1E3A5F] disabled:opacity-30" data-testid={`album-down-${album.id}`}><ChevronDown size={16} /></button>
                  </div>
                  <h3 className="font-bold text-lg">{album.title} - {t('week')} {album.week}</h3>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setAddingPhotoTo(album.id); setPhotoInput({ url: '', caption: '', mode: 'url' }); }} className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200" data-testid={`add-photo-${album.id}`}>
                    <Plus size={14} /> {t('addPhoto')}
                  </button>
                  <button onClick={() => setEditingAlbum({...album})} className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-album-${album.id}`}><Edit2 size={16} /></button>
                  <button onClick={async () => { await axios.delete(`${API}/gallery/${album.id}`, { headers }); loadData(); }} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-album-${album.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
              {album.description && <p className="text-sm text-slate-500 mb-4">{album.description}</p>}

              {addingPhotoTo === album.id && (
                <div className="bg-slate-50 p-4 rounded-lg mb-4">
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setPhotoInput({...photoInput, mode: 'url'})} className={`px-3 py-1 rounded text-sm ${photoInput.mode === 'url' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-200'}`}>{t('externalUrl')}</button>
                    <button onClick={() => setPhotoInput({...photoInput, mode: 'upload'})} className={`px-3 py-1 rounded text-sm ${photoInput.mode === 'upload' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-200'}`}>{t('uploadPhoto')}</button>
                  </div>
                  {photoInput.mode === 'url' ? (
                    <input type="text" placeholder="https://..." value={photoInput.url} onChange={e => setPhotoInput({...photoInput, url: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-2" data-testid="photo-url-input" />
                  ) : (
                    <input type="file" accept="image/*" ref={photoFileRef} className="mb-2" data-testid="photo-file-input" />
                  )}
                  <input type="text" placeholder={language === 'gr' ? 'Λεζάντα (προαιρετικό)' : 'Caption (optional)'} value={photoInput.caption} onChange={e => setPhotoInput({...photoInput, caption: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-2" data-testid="photo-caption-input" />
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      try {
                        const payload = { caption: photoInput.caption };
                        if (photoInput.mode === 'url') {
                          payload.url = photoInput.url;
                        } else if (photoFileRef.current?.files[0]) {
                          const reader = new FileReader();
                          reader.onloadend = async () => {
                            await axios.post(`${API}/gallery/${album.id}/photos`, { caption: photoInput.caption, photo_base64: reader.result }, { headers });
                            setAddingPhotoTo(null);
                            loadData();
                          };
                          reader.readAsDataURL(photoFileRef.current.files[0]);
                          return;
                        }
                        await axios.post(`${API}/gallery/${album.id}/photos`, payload, { headers });
                        setAddingPhotoTo(null);
                        loadData();
                      } catch(e) { console.error(e); }
                    }} className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm" data-testid="save-photo-btn"><Save size={16} /></button>
                    <button onClick={() => setAddingPhotoTo(null)} className="px-4 py-2 bg-slate-200 rounded-lg text-sm"><XCircle size={16} /></button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {album.photos.map((photo, photoIdx) => (
                  <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden">
                    <img src={photo.is_upload ? `${BACKEND_URL}${photo.url}` : photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      {photoIdx > 0 && <button onClick={async () => { const ids = album.photos.map(p => p.id); [ids[photoIdx], ids[photoIdx-1]] = [ids[photoIdx-1], ids[photoIdx]]; await axios.put(`${API}/gallery/${album.id}/reorder-photos`, ids, { headers }); loadData(); }} className="bg-white/90 text-slate-700 rounded-full p-1 hover:bg-white"><ChevronUp size={12} /></button>}
                      {photoIdx < album.photos.length - 1 && <button onClick={async () => { const ids = album.photos.map(p => p.id); [ids[photoIdx], ids[photoIdx+1]] = [ids[photoIdx+1], ids[photoIdx]]; await axios.put(`${API}/gallery/${album.id}/reorder-photos`, ids, { headers }); loadData(); }} className="bg-white/90 text-slate-700 rounded-full p-1 hover:bg-white"><ChevronDown size={12} /></button>}
                      <button onClick={async () => { await axios.delete(`${API}/gallery/${album.id}/photos/${photo.id}`, { headers }); loadData(); }} className="bg-red-500 text-white rounded-full p-1"><Trash2 size={12} /></button>
                    </div>
                    {photo.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1">{photo.caption}</div>}
                    <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition">{photoIdx + 1}</div>
                  </div>
                ))}
              </div>
              {album.photos.length === 0 && <p className="text-slate-400 text-sm text-center py-4">{t('noPhotos')}</p>}
            </div>
          ))}

          {galleryAlbums.length === 0 && !newAlbum && (
            <p className="text-slate-400 text-center py-8">{t('noPhotos')}</p>
          )}

          {/* Edit Album Modal */}
          {editingAlbum && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingAlbum(null)}>
              <div className="bg-white rounded-xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold mb-4">{language === 'gr' ? 'Επεξεργασία Άλμπουμ' : 'Edit Album'}</h3>
                <input type="text" value={editingAlbum.title} onChange={e => setEditingAlbum({...editingAlbum, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" placeholder={t('announcementTitle')} data-testid="edit-album-title" />
                <input type="number" value={editingAlbum.week} onChange={e => setEditingAlbum({...editingAlbum, week: parseInt(e.target.value) || 1})} className="w-full px-3 py-2 border rounded-lg mb-3" placeholder={t('week')} />
                <textarea value={editingAlbum.description || ''} onChange={e => setEditingAlbum({...editingAlbum, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-20" placeholder={language === 'gr' ? 'Περιγραφή (προαιρετικό)' : 'Description (optional)'} data-testid="edit-album-desc" />
                <div className="flex gap-2">
                  <button onClick={async () => { await axios.put(`${API}/gallery/${editingAlbum.id}`, { title: editingAlbum.title, week: editingAlbum.week, description: editingAlbum.description || '' }, { headers }); setEditingAlbum(null); loadData(); }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg"><Save size={16} /> {t('save')}</button>
                  <button onClick={() => setEditingAlbum(null)} className="px-4 py-2 bg-slate-200 rounded-lg">{t('cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Videos Section */}
          <div className="mt-8 pt-8 border-t-2 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Youtube size={22} className="text-red-500" /> {t('videos')}</h3>
              <button onClick={() => setNewVideo({ title: '', youtube_url: '', description: '', season: adminSeason })} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600" data-testid="create-video-btn">
                <Plus size={18} /> {t('addVideo')}
              </button>
            </div>

            {newVideo && (
              <div className="card p-6 border-2 border-red-400 mb-4">
                <h4 className="font-bold mb-3">{t('addVideo')}</h4>
                <input type="text" placeholder={t('videoTitle')} value={newVideo.title} onChange={e => setNewVideo({...newVideo, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" data-testid="new-video-title" />
                <input type="text" placeholder={t('videoUrl')} value={newVideo.youtube_url} onChange={e => setNewVideo({...newVideo, youtube_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" data-testid="new-video-url" />
                <textarea placeholder={t('videoDescription')} value={newVideo.description} onChange={e => setNewVideo({...newVideo, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-20" data-testid="new-video-desc" />
                <div className="flex gap-2">
                  <button onClick={async () => { try { await axios.post(`${API}/videos`, {...newVideo, season: adminSeason}, { headers }); setNewVideo(null); loadData(); } catch(e) { console.error(e); }}} className="px-4 py-2 bg-green-500 text-white rounded-lg" data-testid="save-video-btn"><Save size={16} /></button>
                  <button onClick={() => setNewVideo(null)} className="px-4 py-2 bg-slate-200 rounded-lg"><XCircle size={16} /></button>
                </div>
              </div>
            )}

            {videos.map((video, videoIdx) => (
              <div key={video.id} className="card p-4 flex items-center justify-between mb-3" data-testid={`admin-video-${video.id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button disabled={videoIdx === 0} onClick={async () => { const ids = videos.map(v => v.id); [ids[videoIdx], ids[videoIdx-1]] = [ids[videoIdx-1], ids[videoIdx]]; await axios.put(`${API}/videos/reorder`, ids, { headers }); loadData(); }} className="p-0.5 text-slate-400 hover:text-[#1E3A5F] disabled:opacity-30" data-testid={`video-up-${video.id}`}><ChevronUp size={14} /></button>
                    <button disabled={videoIdx === videos.length - 1} onClick={async () => { const ids = videos.map(v => v.id); [ids[videoIdx], ids[videoIdx+1]] = [ids[videoIdx+1], ids[videoIdx]]; await axios.put(`${API}/videos/reorder`, ids, { headers }); loadData(); }} className="p-0.5 text-slate-400 hover:text-[#1E3A5F] disabled:opacity-30" data-testid={`video-down-${video.id}`}><ChevronDown size={14} /></button>
                  </div>
                  <Youtube size={24} className="text-red-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <h4 className="font-bold truncate">{video.title}</h4>
                    <p className="text-xs text-slate-400 truncate">{video.youtube_url}</p>
                    {video.description && <p className="text-sm text-slate-500 truncate">{video.description}</p>}
                  </div>
                </div>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setEditingVideo({...video})} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={16} /></button>
                  <button onClick={async () => { await axios.delete(`${API}/videos/${video.id}`, { headers }); loadData(); }} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-video-${video.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}

            {videos.length === 0 && !newVideo && (
              <p className="text-slate-400 text-center py-4">{t('noVideos')}</p>
            )}
          </div>

          {/* Edit Video Modal */}
          {editingVideo && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingVideo(null)}>
              <div className="bg-white rounded-xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold mb-4">{language === 'gr' ? 'Επεξεργασία Βίντεο' : 'Edit Video'}</h3>
                <input type="text" value={editingVideo.title} onChange={e => setEditingVideo({...editingVideo, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" placeholder={t('videoTitle')} data-testid="edit-video-title" />
                <input type="text" value={editingVideo.youtube_url} onChange={e => setEditingVideo({...editingVideo, youtube_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3" placeholder={t('videoUrl')} />
                <textarea value={editingVideo.description} onChange={e => setEditingVideo({...editingVideo, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg mb-3 h-20" placeholder={t('videoDescription')} />
                <div className="flex gap-2">
                  <button onClick={async () => { await axios.put(`${API}/videos/${editingVideo.id}`, { title: editingVideo.title, youtube_url: editingVideo.youtube_url, description: editingVideo.description }, { headers }); setEditingVideo(null); loadData(); }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg"><Save size={16} /> {t('save')}</button>
                  <button onClick={() => setEditingVideo(null)} className="px-4 py-2 bg-slate-200 rounded-lg">{t('cancel')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SPONSORS ADMIN TAB */}
      {activeTab === 'sponsors' && (
        <div className="space-y-6" data-testid="admin-sponsors">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center">
                  <Star className="text-[#F97316]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Χορηγοί' : 'Sponsors'}</h2>
                  <p className="text-sm text-slate-500">{language === 'gr' ? 'Διαχείριση χορηγών με λογότυπο και σύνδεσμο' : 'Manage sponsors with logo and link'}</p>
                </div>
              </div>
              <button
                onClick={() => setNewSponsor({ name: '', title: '', url: '', logo_base64: '', order: sponsors.length })}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                data-testid="add-sponsor-btn"
              >
                <Plus size={18} /> {language === 'gr' ? 'Προσθήκη Χορηγού' : 'Add Sponsor'}
              </button>
            </div>

            {/* New Sponsor Form */}
            {newSponsor && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6" data-testid="new-sponsor-form">
                <h3 className="font-bold mb-4">{language === 'gr' ? 'Νέος Χορηγός' : 'New Sponsor'}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Τίτλος (π.χ. Επίσημος Χορηγός)' : 'Title (e.g. Official Sponsor)'}</label>
                    <input type="text" value={newSponsor.title} onChange={e => setNewSponsor({...newSponsor, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder={language === 'gr' ? 'Επίσημος Χορηγός' : 'Official Sponsor'} data-testid="new-sponsor-title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Όνομα' : 'Name'}</label>
                    <input type="text" value={newSponsor.name} onChange={e => setNewSponsor({...newSponsor, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-sponsor-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'URL (backlink)' : 'URL (backlink)'}</label>
                    <input type="text" value={newSponsor.url} onChange={e => setNewSponsor({...newSponsor, url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="https://www.example.com" data-testid="new-sponsor-url" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Λογότυπο' : 'Logo'}</label>
                    <input type="file" ref={newSponsorFileRef} accept="image/*" onChange={e => handleSponsorLogo(e, true)} className="hidden" />
                    <div className="flex items-center gap-3">
                      <button onClick={() => newSponsorFileRef.current?.click()} className="flex items-center gap-2 px-3 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 text-sm" data-testid="new-sponsor-upload">
                        <Upload size={16} /> {language === 'gr' ? 'Ανέβασμα' : 'Upload'}
                      </button>
                      {newSponsor.logo_base64 && <img src={newSponsor.logo_base64} alt="preview" className="h-12 object-contain" />}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={createSponsor} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-sponsor-btn">
                      <Save size={16} /> {language === 'gr' ? 'Δημιουργία' : 'Create'}
                    </button>
                    <button onClick={() => setNewSponsor(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-new-sponsor-btn">
                      <XCircle size={16} /> {t('cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sponsors List */}
            {sponsors.length === 0 && !newSponsor && (
              <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν χορηγοί. Πατήστε "Προσθήκη Χορηγού".' : 'No sponsors. Click "Add Sponsor".'}</p>
            )}

            <div className="space-y-4">
              {sponsors.map(sponsor => (
                <div key={sponsor.id} className="bg-slate-50 rounded-xl p-5" data-testid={`sponsor-card-${sponsor.id}`}>
                  {editingSponsor?.id === sponsor.id ? (
                    <div className="space-y-4" data-testid="sponsor-edit-form">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Τίτλος (π.χ. Επίσημος Χορηγός)' : 'Title (e.g. Official Sponsor)'}</label>
                        <input type="text" value={editingSponsor.title || ''} onChange={e => setEditingSponsor({...editingSponsor, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder={language === 'gr' ? 'Επίσημος Χορηγός' : 'Official Sponsor'} data-testid="sponsor-title" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Όνομα' : 'Name'}</label>
                        <input type="text" value={editingSponsor.name || ''} onChange={e => setEditingSponsor({...editingSponsor, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="sponsor-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">URL (backlink)</label>
                        <input type="text" value={editingSponsor.url || ''} onChange={e => setEditingSponsor({...editingSponsor, url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="sponsor-url" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Λογότυπο' : 'Logo'}</label>
                        <input type="file" ref={sponsorFileRef} accept="image/*" onChange={e => handleSponsorLogo(e, false)} className="hidden" />
                        <div className="flex items-center gap-3">
                          <button onClick={() => sponsorFileRef.current?.click()} className="flex items-center gap-2 px-3 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 text-sm" data-testid="sponsor-upload-logo">
                            <Upload size={16} /> {language === 'gr' ? 'Αλλαγή' : 'Change'}
                          </button>
                          {(editingSponsor.logo_base64 || sponsor.has_logo) && (
                            <img src={editingSponsor.logo_base64 || `${BACKEND_URL}/api/sponsors/${sponsor.id}/logo`} alt="preview" className="h-12 object-contain" />
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-4 border-t">
                        <button onClick={saveSponsor} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-sponsor-btn">
                          <Save size={16} /> {t('save')}
                        </button>
                        <button onClick={() => setEditingSponsor(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-sponsor-btn">
                          <XCircle size={16} /> {t('cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {sponsor.has_logo ? (
                          <img src={`${BACKEND_URL}/api/sponsors/${sponsor.id}/logo`} alt={sponsor.name} className="h-14 w-auto object-contain bg-white rounded-lg p-1" />
                        ) : (
                          <div className="h-14 w-14 bg-slate-200 rounded-lg flex items-center justify-center"><Star size={24} className="text-slate-400" /></div>
                        )}
                        <div>
                          <h3 className="font-bold text-[#1E3A5F]">{sponsor.name}</h3>
                          {sponsor.title && <p className="text-xs text-[#F97316] font-medium">{sponsor.title}</p>}
                          {sponsor.url && (
                            <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#F97316] hover:underline flex items-center gap-1">
                              <ExternalLink size={12} /> {sponsor.url}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingSponsor({...sponsor})} className="flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8f] text-sm" data-testid={`edit-sponsor-${sponsor.id}`}>
                          <Edit2 size={16} /> {t('edit')}
                        </button>
                        <button onClick={() => setDeleteConfirm({ type: 'sponsor', id: sponsor.id, name: sponsor.name })} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-sponsor-${sponsor.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* SETTINGS TAB - Season Facts */}
      {activeTab === 'settings' && (
        <div className="space-y-6" data-testid="admin-settings">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
                  <Trophy className="text-[#1E3A5F]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Στοιχεία Σεζόν 1' : 'Season 1 Facts'}</h2>
                  <p className="text-sm text-slate-500">{t('editSeasonFacts')}</p>
                </div>
              </div>
              {!editingSeasonSettings && (
                <button
                  onClick={() => setEditingSeasonSettings({...seasonSettings})}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8f]"
                  data-testid="edit-season-settings-btn"
                >
                  <Edit2 size={18} /> {t('edit')}
                </button>
              )}
            </div>

            {editingSeasonSettings ? (
              <div className="space-y-4" data-testid="season-settings-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('teamsCount')}</label>
                    <input 
                      type="text" 
                      value={editingSeasonSettings.teams_count} 
                      onChange={e => setEditingSeasonSettings({...editingSeasonSettings, teams_count: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      data-testid="season-teams-count"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('playersCount')}</label>
                    <input 
                      type="text" 
                      value={editingSeasonSettings.players_count} 
                      onChange={e => setEditingSeasonSettings({...editingSeasonSettings, players_count: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      data-testid="season-players-count"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('gamesCount')}</label>
                    <input 
                      type="text" 
                      value={editingSeasonSettings.games_count} 
                      onChange={e => setEditingSeasonSettings({...editingSeasonSettings, games_count: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      data-testid="season-games-count"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('champion')}</label>
                    <input 
                      type="text" 
                      value={editingSeasonSettings.champion} 
                      onChange={e => setEditingSeasonSettings({...editingSeasonSettings, champion: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      data-testid="season-champion"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <svg className="inline w-4 h-4 mr-1 text-red-600" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                    YouTube {language === 'gr' ? 'Κανάλι' : 'Channel'}
                  </label>
                  <input 
                    type="text" 
                    value={editingSeasonSettings.youtube_url || ''} 
                    onChange={e => setEditingSeasonSettings({...editingSeasonSettings, youtube_url: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.youtube.com/@WBLAthens"
                    data-testid="season-youtube-url"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button 
                    onClick={saveSeasonSettings} 
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    data-testid="save-season-settings-btn"
                  >
                    <Save size={16} /> {t('save')}
                  </button>
                  <button 
                    onClick={() => setEditingSeasonSettings(null)} 
                    className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                    data-testid="cancel-season-settings-btn"
                  >
                    <XCircle size={16} /> {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center py-8 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-4xl font-display font-bold text-[#F97316]" data-testid="display-teams-count">{seasonSettings.teams_count}</p>
                    <p className="text-slate-600">{t('teamsCount')}</p>
                  </div>
                  <div>
                    <p className="text-4xl font-display font-bold text-[#F97316]" data-testid="display-players-count">{seasonSettings.players_count}</p>
                    <p className="text-slate-600">{t('playersCount')}</p>
                  </div>
                  <div>
                    <p className="text-4xl font-display font-bold text-[#F97316]" data-testid="display-games-count">{seasonSettings.games_count}</p>
                    <p className="text-slate-600">{t('gamesCount')}</p>
                  </div>
                  <div>
                    <p className="text-4xl font-display font-bold text-[#F97316]" data-testid="display-champion">{seasonSettings.champion}</p>
                    <p className="text-slate-600">{t('champion')}</p>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-3">
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  <div>
                    <p className="text-xs font-medium text-slate-400 mb-0.5">YouTube {language === 'gr' ? 'Κανάλι' : 'Channel'}</p>
                    <p className="text-slate-700 text-sm" data-testid="display-youtube-url">{seasonSettings.youtube_url || <span className="italic text-slate-400">{language === 'gr' ? '(Δεν έχει οριστεί)' : '(Not set)'}</span>}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Season 2 Settings */}
          <div className="card p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center">
                <Settings className="text-[#F97316]" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#F97316]">{language === 'gr' ? 'Ρυθμίσεις Σεζόν 2' : 'Season 2 Settings'}</h2>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Στοιχεία 2ης σεζόν (αρχική σελίδα)' : 'Season 2 data (homepage)'}</p>
              </div>
            </div>
            {!editingSeason2Settings && (
              <button onClick={() => setEditingSeason2Settings({...season2Settings})} className="flex items-center gap-2 px-4 py-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C]" data-testid="edit-season2-btn">
                <Edit2 size={18} /> {t('edit')}
              </button>
            )}
          </div>
          {editingSeason2Settings ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Ομάδες' : 'Teams'}</label>
                  <input type="text" value={editingSeason2Settings.teams_count} onChange={e => setEditingSeason2Settings({...editingSeason2Settings, teams_count: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="season2-teams" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Παίκτριες' : 'Players'}</label>
                  <input type="text" value={editingSeason2Settings.players_count} onChange={e => setEditingSeason2Settings({...editingSeason2Settings, players_count: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="season2-players" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Αγώνες' : 'Games'}</label>
                  <input type="text" value={editingSeason2Settings.games_count} onChange={e => setEditingSeason2Settings({...editingSeason2Settings, games_count: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="season2-games" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Πρωταθλητής' : 'Champion'}</label>
                  <input type="text" value={editingSeason2Settings.champion} onChange={e => setEditingSeason2Settings({...editingSeason2Settings, champion: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="season2-champion" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={saveSeason2Settings} data-testid="save-season2-btn" className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"><Save size={16} /> {t('save')}</button>
                <button onClick={() => setEditingSeason2Settings(null)} data-testid="cancel-season2-btn" className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"><XCircle size={16} /> {t('cancel')}</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center py-8 bg-orange-50 rounded-xl">
              <div>
                <p className="text-3xl font-bold text-[#F97316]">{season2Settings.teams_count}</p>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Ομάδες' : 'Teams'}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#F97316]">{season2Settings.players_count}</p>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Παίκτριες' : 'Players'}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#F97316]">{season2Settings.games_count}</p>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Αγώνες' : 'Games'}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#F97316]">{season2Settings.champion}</p>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Πρωταθλητής' : 'Champion'}</p>
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* ABOUT PAGE CONTENT TAB */}
      {activeTab === 'about' && (
        <div className="space-y-6" data-testid="admin-about">
          <DocumentsManager headers={headers} />
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
                  <BookOpen className="text-[#1E3A5F]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Περιεχόμενο Σελίδας "Σχετικά"' : 'About Page Content'}</h2>
                  <p className="text-sm text-slate-500">{language === 'gr' ? 'Επεξεργαστείτε τα κείμενα της σελίδας Σχετικά' : 'Edit the text content of the About page'}</p>
                </div>
              </div>
              {!editingAbout && (
                <button
                  onClick={() => setEditingAbout({...aboutSections})}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8f]"
                  data-testid="edit-about-btn"
                >
                  <Edit2 size={18} /> {t('edit')}
                </button>
              )}
            </div>

            {editingAbout ? (
              <div className="space-y-4" data-testid="about-edit-form">
                {aboutFields.map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{field.label}</label>
                    {field.small ? (
                      <input
                        type="text"
                        value={editingAbout[field.key] || ''}
                        onChange={e => setEditingAbout({...editingAbout, [field.key]: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        data-testid={`about-field-${field.key}`}
                      />
                    ) : (
                      <textarea
                        value={editingAbout[field.key] || ''}
                        onChange={e => setEditingAbout({...editingAbout, [field.key]: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg min-h-[100px]"
                        rows={4}
                        data-testid={`about-field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <button
                    onClick={saveAboutContent}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    data-testid="save-about-btn"
                  >
                    <Save size={16} /> {t('save')}
                  </button>
                  <button
                    onClick={() => setEditingAbout(null)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                    data-testid="cancel-about-btn"
                  >
                    <XCircle size={16} /> {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="about-preview">
                {aboutFields.map(field => (
                  <div key={field.key} className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-slate-400 mb-1">{field.label}</p>
                    <p className="text-slate-700 whitespace-pre-line">{aboutSections[field.key] || <span className="italic text-slate-400">{language === 'gr' ? '(Προεπιλεγμένο κείμενο)' : '(Default text)'}</span>}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ ADMIN TAB */}
      {activeTab === 'faq' && (
        <div className="space-y-6" data-testid="admin-faq">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
                  <Info className="text-[#1E3A5F]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Συχνές Ερωτήσεις' : 'FAQ Management'}</h2>
                  <p className="text-sm text-slate-500">{language === 'gr' ? 'Διαχειριστείτε τις ερωτήσεις/απαντήσεις στη σελίδα FAQ' : 'Manage FAQ page questions and answers'}</p>
                </div>
              </div>
              {!editingFaq && (
                <button
                  onClick={() => setEditingFaq(faqItems.length > 0 ? [...faqItems] : [{ id: '', question: '', answer: '', order: 0 }])}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8f]"
                  data-testid="edit-faq-btn"
                >
                  <Edit2 size={18} /> {t('edit')}
                </button>
              )}
            </div>

            {editingFaq ? (
              <div className="space-y-4" data-testid="faq-edit-form">
                {editingFaq.map((item, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-4 relative">
                    <button
                      onClick={() => removeFaqItem(idx)}
                      className="absolute top-2 right-2 text-red-400 hover:text-red-600"
                      data-testid={`remove-faq-${idx}`}
                    >
                      <XCircle size={20} />
                    </button>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? `Ερώτηση ${idx + 1}` : `Question ${idx + 1}`}</label>
                    <input
                      type="text"
                      value={item.question || ''}
                      onChange={e => updateFaqItem(idx, 'question', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg mb-2"
                      placeholder={language === 'gr' ? 'Γράψτε την ερώτηση...' : 'Enter question...'}
                      data-testid={`faq-question-${idx}`}
                    />
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Απάντηση' : 'Answer'}</label>
                    <textarea
                      value={item.answer || ''}
                      onChange={e => updateFaqItem(idx, 'answer', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg min-h-[80px]"
                      rows={3}
                      placeholder={language === 'gr' ? 'Γράψτε την απάντηση...' : 'Enter answer...'}
                      data-testid={`faq-answer-${idx}`}
                    />
                  </div>
                ))}
                <button
                  onClick={addFaqItem}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:border-[#1E3A5F] hover:text-[#1E3A5F] w-full justify-center"
                  data-testid="add-faq-item-btn"
                >
                  <Plus size={18} /> {language === 'gr' ? 'Προσθήκη Ερώτησης' : 'Add Question'}
                </button>
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <button onClick={saveFaq} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-faq-btn">
                    <Save size={16} /> {t('save')}
                  </button>
                  <button onClick={() => setEditingFaq(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-faq-btn">
                    <XCircle size={16} /> {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="faq-preview">
                {faqItems.length === 0 ? (
                  <p className="text-slate-400 italic text-center py-8">{language === 'gr' ? 'Χρησιμοποιούνται οι προεπιλεγμένες ερωτήσεις. Πατήστε "Επεξεργασία" για να τις αλλάξετε.' : 'Using default questions. Click "Edit" to customize.'}</p>
                ) : (
                  faqItems.map((item, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-4">
                      <p className="font-medium text-[#1E3A5F]">{item.question}</p>
                      <p className="text-slate-600 text-sm mt-1">{item.answer}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* VENUE ADMIN TAB */}
      {activeTab === 'venue' && (
        <div className="space-y-6" data-testid="admin-venue">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
                  <MapPin className="text-[#1E3A5F]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Γήπεδα Αγώνων' : 'Game Venues'}</h2>
                  <p className="text-sm text-slate-500">{language === 'gr' ? 'Διαχείριση γηπέδων' : 'Manage venues'}</p>
                </div>
              </div>
              <button
                onClick={() => setNewVenue({ name: '', address: '', map_embed: '', description: '', image_url: '' })}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                data-testid="add-venue-btn"
              >
                <Plus size={18} /> {language === 'gr' ? 'Προσθήκη Γηπέδου' : 'Add Venue'}
              </button>
            </div>
            <VenuesTitleEditor headers={headers} />

            {/* New Venue Form */}
            {newVenue && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6" data-testid="new-venue-form">
                <h3 className="font-bold mb-4">{language === 'gr' ? 'Νέο Γήπεδο' : 'New Venue'}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Όνομα Γηπέδου' : 'Venue Name'}</label>
                    <input type="text" value={newVenue.name} onChange={e => setNewVenue({...newVenue, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-venue-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Διεύθυνση' : 'Address'}</label>
                    <input type="text" value={newVenue.address} onChange={e => setNewVenue({...newVenue, address: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-venue-address" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Περιγραφή' : 'Description'}</label>
                    <textarea value={newVenue.description} onChange={e => setNewVenue({...newVenue, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg min-h-[80px]" rows={3} data-testid="new-venue-description" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Google Maps Embed URL</label>
                    <input type="text" value={newVenue.map_embed} onChange={e => setNewVenue({...newVenue, map_embed: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="https://www.google.com/maps/embed?pb=..." data-testid="new-venue-map" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={createVenue} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-new-venue-btn">
                      <Save size={16} /> {t('create')}
                    </button>
                    <button onClick={() => setNewVenue(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-new-venue-btn">
                      <XCircle size={16} /> {t('cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Venues List */}
            {venuesList.length === 0 && !newVenue && (
              <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν γήπεδα. Πατήστε "Προσθήκη Γηπέδου" για να δημιουργήσετε ένα.' : 'No venues. Click "Add Venue" to create one.'}</p>
            )}

            <div className="space-y-4">
              {venuesList.map(venue => (
                <div key={venue.id} className="bg-slate-50 rounded-xl p-5" data-testid={`venue-card-${venue.id}`}>
                  {editingVenue?.id === venue.id ? (
                    <div className="space-y-4" data-testid="venue-edit-form">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Όνομα Γηπέδου' : 'Venue Name'}</label>
                        <input type="text" value={editingVenue.name || ''} onChange={e => setEditingVenue({...editingVenue, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="venue-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Διεύθυνση' : 'Address'}</label>
                        <input type="text" value={editingVenue.address || ''} onChange={e => setEditingVenue({...editingVenue, address: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="venue-address" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{language === 'gr' ? 'Περιγραφή' : 'Description'}</label>
                        <textarea value={editingVenue.description || ''} onChange={e => setEditingVenue({...editingVenue, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg min-h-[80px]" rows={3} data-testid="venue-description" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Google Maps Embed URL</label>
                        <input type="text" value={editingVenue.map_embed || ''} onChange={e => setEditingVenue({...editingVenue, map_embed: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="venue-map-embed" />
                      </div>
                      <div className="flex gap-2 justify-end pt-4 border-t">
                        <button onClick={saveVenue} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="save-venue-btn">
                          <Save size={16} /> {t('save')}
                        </button>
                        <button onClick={() => setEditingVenue(null)} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300" data-testid="cancel-venue-btn">
                          <XCircle size={16} /> {t('cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg text-[#1E3A5F]">{venue.name || <span className="italic text-slate-400">{language === 'gr' ? '(Χωρίς όνομα)' : '(No name)'}</span>}</h3>
                          {venue.address && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin size={14} /> {venue.address}</p>}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingVenue({...venue})} className="flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8f] text-sm" data-testid={`edit-venue-${venue.id}`}>
                            <Edit2 size={16} /> {t('edit')}
                          </button>
                          <button onClick={() => setDeleteConfirm({ type: 'venue', id: venue.id, name: venue.name || 'Venue' })} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-venue-${venue.id}`}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      {venue.description && <p className="text-sm text-slate-600 mb-3">{venue.description}</p>}
                      {venue.map_embed && (
                        <div className="rounded-xl overflow-hidden">
                          <iframe src={venue.map_embed} width="100%" height="200" style={{ border: 0 }} loading="lazy" title={venue.name}></iframe>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PLAYER TRANSFER MODAL */}
      {transferringPlayer && selectedTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="transfer-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <ArrowRightLeft className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg">{language === 'gr' ? 'Μεταγραφή Παίκτριας' : 'Player Transfer'}</h3>
                <p className="text-sm text-slate-500">{transferringPlayer.name} (#{transferringPlayer.number})</p>
              </div>
            </div>

            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">{language === 'gr' ? 'Από' : 'From'}</p>
              <p className="font-medium text-[#1E3A5F]">{selectedTeam.name}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">{language === 'gr' ? 'Προς Ομάδα' : 'To Team'}</label>
              <select
                value={transferTargetTeam}
                onChange={e => setTransferTargetTeam(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="transfer-target-team"
              >
                <option value="">{language === 'gr' ? 'Επιλέξτε ομάδα...' : 'Select team...'}</option>
                {teams.filter(tm => tm.id !== selectedTeam.id).map(tm => (
                  <option key={tm.id} value={tm.id}>{tm.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setTransferringPlayer(null); setTransferTargetTeam(''); }}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
                data-testid="cancel-transfer-btn"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleTransfer}
                disabled={!transferTargetTeam}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirm-transfer-btn"
              >
                <ArrowRightLeft size={18} /> {language === 'gr' ? 'Μεταγραφή' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SYNC TO LIVE TAB */}
      {activeTab === 'sync' && (
        <div className="space-y-6" data-testid="admin-sync">
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <RefreshCw className="text-green-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">{language === 'gr' ? 'Συγχρονισμός στο Live Site' : 'Sync to Live Site'}</h2>
                <p className="text-sm text-slate-500">{language === 'gr' ? 'Αντιγραφή όλων των δεδομένων από Preview στο wbl.gr' : 'Copy all data from Preview to wbl.gr'}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800 mb-1">{language === 'gr' ? 'Προσοχή' : 'Warning'}</p>
                  <p className="text-sm text-amber-700">
                    {language === 'gr' 
                      ? 'Αυτή η ενέργεια θα αντικαταστήσει ΟΛΑ τα δεδομένα στο live site (wbl.gr) με αυτά του Preview. Περιλαμβάνει: ομάδες, παίκτριες, αγώνες, ανακοινώσεις (+ φωτογραφίες), γήπεδα, χορηγούς, ρυθμίσεις.' 
                      : 'This will replace ALL data on the live site (wbl.gr) with Preview data. Includes: teams, players, games, announcements (+ photos), venues, sponsors, settings.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Sync Button */}
            <button
              onClick={async () => {
                if (syncStatus?.running) return;
                if (!window.confirm(language === 'gr' ? 'Σίγουρα θέλετε να ξεκινήσει ο συγχρονισμός;' : 'Are you sure you want to start sync?')) return;
                try {
                  const res = await axios.post(`${API}/sync-to-live`, {}, { headers });
                  setSyncStatus(res.data.status);
                  setSyncPolling(true);
                  // Start polling
                  const poll = setInterval(async () => {
                    try {
                      const statusRes = await axios.get(`${API}/sync-status`, { headers });
                      setSyncStatus(statusRes.data);
                      if (!statusRes.data.running) {
                        clearInterval(poll);
                        setSyncPolling(false);
                      }
                    } catch (e) {
                      clearInterval(poll);
                      setSyncPolling(false);
                    }
                  }, 2000);
                } catch (e) {
                  alert(e.response?.data?.detail || 'Sync failed to start');
                }
              }}
              disabled={syncStatus?.running}
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition ${
                syncStatus?.running 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
              }`}
              data-testid="start-sync-btn"
            >
              <RefreshCw size={24} className={syncStatus?.running ? 'animate-spin' : ''} />
              {syncStatus?.running 
                ? (language === 'gr' ? 'Συγχρονισμός σε εξέλιξη...' : 'Sync in progress...') 
                : (language === 'gr' ? 'Έναρξη Συγχρονισμού' : 'Start Sync')}
            </button>

            {/* Progress */}
            {syncStatus && (
              <div className="mt-6 space-y-4">
                {/* Progress Bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">{syncStatus.step}</span>
                    <span className="text-sm font-bold text-[#1E3A5F]">{syncStatus.progress}%</span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${syncStatus.error ? 'bg-red-500' : syncStatus.completed ? 'bg-green-500' : 'bg-[#F97316]'}`}
                      style={{ width: `${syncStatus.progress}%` }}
                    />
                  </div>
                </div>

                {/* Status Badge */}
                {syncStatus.completed && !syncStatus.error && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center text-green-700 font-bold text-lg">
                      &#10003;
                    </div>
                    <div>
                      <p className="font-bold text-green-800">{language === 'gr' ? 'Ολοκληρώθηκε!' : 'Complete!'}</p>
                      <p className="text-sm text-green-600">{language === 'gr' ? 'Τα δεδομένα συγχρονίστηκαν στο wbl.gr' : 'Data synced to wbl.gr'}</p>
                    </div>
                  </div>
                )}

                {syncStatus.error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="font-bold text-red-800 mb-1">{language === 'gr' ? 'Σφάλμα' : 'Error'}</p>
                    <p className="text-sm text-red-600">{syncStatus.error}</p>
                  </div>
                )}

                {/* Log */}
                {syncStatus.log?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">{language === 'gr' ? 'Αρχείο Ενεργειών' : 'Activity Log'}</h4>
                    <div className="bg-slate-900 text-green-400 rounded-xl p-4 max-h-64 overflow-y-auto font-mono text-xs space-y-1" data-testid="sync-log">
                      {syncStatus.log.map((msg, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="text-slate-500 shrink-0">[{String(i + 1).padStart(2, '0')}]</span>
                          <span>{msg}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                {syncStatus.started_at && (
                  <div className="text-xs text-slate-400 flex gap-4">
                    <span>{language === 'gr' ? 'Έναρξη' : 'Started'}: {new Date(syncStatus.started_at).toLocaleString('el-GR')}</span>
                    {syncStatus.finished_at && (
                      <span>{language === 'gr' ? 'Ολοκλήρωση' : 'Finished'}: {new Date(syncStatus.finished_at).toLocaleString('el-GR')}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Per-Game Stats Modal */}
      {gameStatsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setGameStatsModal(null)}>
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto my-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">
              {language === 'gr' ? 'Στατιστικά Αγώνα' : 'Game Stats'} - {language === 'gr' ? 'Αγωνιστική' : 'MW'} {gameStatsModal.week}
            </h3>
            <p className="text-sm text-slate-500 mb-2">{gameStatsModal.home_team_name} vs {gameStatsModal.away_team_name} ({gameStatsModal.date})</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-xs text-green-700">
              {language === 'gr' 
                ? 'Τα συνολικά στατιστικά κάθε παίκτριας υπολογίζονται αυτόματα από τα στατιστικά ανά αγώνα.' 
                : 'Player totals are automatically calculated from per-game stats.'}
            </div>
            
            {[gameStatsModal.home_team_id, gameStatsModal.away_team_id].filter(id => id !== 'TBD').map(teamId => {
              const team = teams.find(t => t.id === teamId);
              if (!team) return null;
              const isHome = teamId === gameStatsModal.home_team_id;
              const opponent = isHome ? gameStatsModal.away_team_name : gameStatsModal.home_team_name;
              return (
                <div key={teamId} className="mb-6">
                  <h4 className="font-bold text-[#1E3A5F] mb-2 flex items-center gap-2">
                    {team.has_logo && <img src={getLogoUrl(team.id)} alt="" className="w-6 h-6 rounded-full" />}
                    {team.name}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">{language === 'gr' ? 'Παίκτρια' : 'Player'}</th>
                          <th className="p-2 text-center">ΠΟΝ</th>
                          <th className="p-2 text-center">Β</th>
                          <th className="p-2 text-center">2Π</th>
                          <th className="p-2 text-center">3Π</th>
                          <th className="p-2 text-center">ΑΡ</th>
                          <th className="p-2 text-center">ΕΡ</th>
                          <th className="p-2 text-center">ΣΡ</th>
                          <th className="p-2 text-center">ΚΛ</th>
                          <th className="p-2 text-center">ΛΑ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(team.players || []).map(player => {
                          const existing = gameStatsData[player.id];
                          const key = player.id;
                          const getVal = (field) => {
                            if (gameStatsData[key]?.stats?.[field] !== undefined) return gameStatsData[key].stats[field];
                            return 0;
                          };
                          const setVal = (field, val) => {
                            const current = gameStatsData[key] || { stats: {} };
                            setGameStatsData({
                              ...gameStatsData,
                              [key]: {
                                ...current,
                                id: current.id,
                                player_id: player.id,
                                player_name: player.name,
                                team_id: teamId,
                                team_name: team.name,
                                game_id: gameStatsModal.id,
                                week: gameStatsModal.week,
                                opponent: opponent,
                                date: gameStatsModal.date,
                                stats: { ...current.stats, [field]: parseInt(val) || 0 }
                              }
                            });
                          };
                          const statFields = ['points','free_throws','two_pointers','three_pointers','def_rebounds','off_rebounds','total_rebounds','steals','turnovers'];
                          return (
                            <tr key={player.id} className="border-b">
                              <td className="p-1 text-center">{player.number}</td>
                              <td className="p-1 whitespace-nowrap text-xs">{player.name}</td>
                              {statFields.map(f => (
                                <td key={f} className="p-1">
                                  <input type="number" min="0" value={getVal(f) || ''} onChange={e => setVal(f, e.target.value)} className="w-10 px-1 py-0.5 border rounded text-center text-xs" />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            
            <div className="flex gap-3 justify-end mt-4 sticky bottom-0 bg-white pt-3 border-t">
              <button onClick={async () => {
                try {
                  const entries = Object.values(gameStatsData).filter(e => e.player_id && e.stats && Object.values(e.stats).some(v => v > 0));
                  if (entries.length === 0) { setGameStatsModal(null); return; }
                  const payload = entries.map(e => ({
                    player_id: e.player_id,
                    player_name: e.player_name,
                    team_id: e.team_id,
                    team_name: e.team_name,
                    game_id: gameStatsModal.id,
                    week: gameStatsModal.week,
                    opponent: e.opponent,
                    date: gameStatsModal.date,
                    stats: e.stats,
                  }));
                  await axios.post(`${API}/game-stats/${gameStatsModal.id}/bulk`, payload, { headers });
                  setGameStatsModal(null);
                } catch(err) { console.error(err); }
              }} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                <Save size={16} /> {language === 'gr' ? 'Αποθήκευση' : 'Save'}
              </button>
              <button onClick={() => setGameStatsModal(null)} className="px-4 py-2 bg-slate-200 rounded-lg">
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
